import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "@scripts/react/components/Button";
import { CardHeader } from "./CardHeader";
import { CardThumbnails, Thumbnail } from "./CardThumbnails";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { getLabelColor, updateLabelColor } from "@rapid/components/annotation/colors";
import { BuildPromptSteps } from "@rapid/types/flows";
import { useInferenceStrategy } from "@rapid/strategies/inference";
import { useModel } from "@rapid/contexts/ModelContext";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useFrame } from "@rapid/contexts/FrameContext";
import type { Annotation } from "@rapid/types/annotation";
import { Chip } from "@scripts/react/components/Chip";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent, AppSource } from "@rapid/analytics/types";
import {
    useCurrentMedia,
    useMediaStore,
    useSyncPromptLabels,
    useMarkLabelAsEdited,
    useAnyLabelHasDiffs,
    useGetBoxPromptCache,
    useSetBoxPromptCache,
    useIncrementBoxPromptCacheIndex,
    useClearBoxPromptCacheForLabel
} from "@rapid/store";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import { ConfidenceSlider } from "@rapid/components/ConfidenceSlider";
import { useMediaUtils } from "@rapid/hooks/useMediaUtils";
import {
    filterByFileId,
    filterByFileAndFrameId,
    annotationsToThumbnails,
    deleteAnnotationsByLabel,
    deleteAnnotationsByLabelCompletely,
    hasRealAnnotations,
    countRealAnnotations
} from "./cardUtils";
import { TooltipContainer } from "@scripts/react/components/Tooltip.jsx";
import { NoResultsEmptyState } from "../NoResultsEmptyState";
import { useGetPredictions } from "@rapid/hooks/useGetPredictions";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";
import {
    buildBoxPromptingData,
    calculateBoxPromptConfidenceThreshold,
    predictionToAnnotation
} from "@rapid/utils/predictions";
import type { Prediction } from "@rapid/utils/predictionFiltering";
import { crossClassNMSWithExisting } from "@rapid/utils/nms";
import {
    getPromptFrameIds,
    waitForPromptFrameUploads
} from "@rapid/hooks/useAutoPredictions/utils";
import { useStore } from "@rapid/store";
import { AnimatePresence, motion } from "framer-motion";

const RELABEL_HINT_KEY = "thumbnail_relabel_hint_seen";

function hasSeenRelabelHint(): boolean {
    return localStorage.getItem(RELABEL_HINT_KEY) === "true";
}

function markRelabelHintAsSeen(): void {
    localStorage.setItem(RELABEL_HINT_KEY, "true");
}

interface PromptLabelAnnotateCardProps {
    name: string;
    annotationsForThisLabel: (Annotation & { frameId: string; fileId: string })[];
    allAnnotationsForThisLabel: (Annotation & { frameId: string; fileId: string })[];
    allAnnotationsIncludingDrafts: (Annotation & { frameId: string; fileId: string })[];
    initialConfidenceThreshold?: number;
    inputRef: React.RefObject<HTMLInputElement | null>;
    initiallyEditing?: boolean;
}

export const PromptLabelAnnotateCard: React.FC<PromptLabelAnnotateCardProps> = ({
    name,
    inputRef,
    initialConfidenceThreshold,
    annotationsForThisLabel,
    allAnnotationsForThisLabel,
    allAnnotationsIncludingDrafts,
    initiallyEditing = false
}) => {
    const {
        setAnnotationsForFileAndFrameId,
        setPredictionsForFileAndFrameSegment,
        textPrompts,
        updatePromptLabels,
        promptLabels,
        clearPredictionsForFile
    } = useModel();
    const syncPromptLabels = useSyncPromptLabels();
    const markLabelAsEdited = useMarkLabelAsEdited();
    const { getPredictionsForFile } = useGetPredictions();
    const {
        setPreviouslySelectedObject,
        setCurrentFrameAnnotations,
        flowLocked,
        setFlowLocked,
        previouslySelectedObject,
        selectedObject
    } = useFlow();
    const { setSelectedAnnotationId, setShouldShowAnnotationInput } = useFrame();
    const { track } = useAnalytics();
    const currentMedia = useCurrentMedia();
    const { setCurrentMedia } = useMediaUtils();
    const media = useMediaStore();
    const anyLabelHasDiffs = useAnyLabelHasDiffs();
    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceUrl = currentWorkspace?.url ?? "anonymous";
    const inferenceStrategy = useInferenceStrategy();
    const getBoxPromptCache = useGetBoxPromptCache();
    const setBoxPromptCache = useSetBoxPromptCache();
    const incrementBoxPromptCacheIndex = useIncrementBoxPromptCacheIndex();
    const clearBoxPromptCacheForLabel = useClearBoxPromptCacheForLabel();

    const [isHovered, setIsHovered] = useState(false);
    const [isEditing, setIsEditing] = useState(initiallyEditing);
    const [editValue, setEditValue] = useState(name);
    const [pendingDelete, setPendingDelete] = useState<{
        annotationId: string;
        frameId: string;
        fileId: string;
    } | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isFindingSimilar, setIsFindingSimilar] = useState(false);
    // Track if we should show the relabel hint and which thumbnail to show it on
    const [showRelabelHint, setShowRelabelHint] = useState(false);
    const [relabelHintThumbnailId, setRelabelHintThumbnailId] = useState<string | null>(null);
    // Track consecutive Find Similar Objects clicks for analytics
    const [findSimilarClickCount, setFindSimilarClickCount] = useState(0);

    // For videos, use the frame ID. For images, use the file ID (same as frameId in annotations)
    const currentFrameId = currentMedia?.frame?.id ?? currentMedia?.file.id;

    // Annotations filtered by confidence threshold and current file/frame
    // We filter by both file AND frame to match what's displayed on the canvas
    // This is especially important for videos where a file can have multiple frames
    const visibleAnnotationsForCurrentFile = useMemo(
        () =>
            filterByFileAndFrameId(annotationsForThisLabel, currentMedia?.file.id, currentFrameId),
        [annotationsForThisLabel, currentMedia?.file.id, currentFrameId]
    );

    // Confirmed annotations for current file/frame (including those filtered out by confidence)
    // This excludes drafts (predictions that haven't been approved yet)
    const allAnnotationsForCurrentFile = useMemo(
        () =>
            filterByFileAndFrameId(
                allAnnotationsForThisLabel,
                currentMedia?.file.id,
                currentFrameId
            ),
        [allAnnotationsForThisLabel, currentMedia?.file.id, currentFrameId]
    );

    // Get all annotations including drafts for current file/frame
    const allAnnotationsIncludingDraftsForCurrentFile = useMemo(
        () =>
            filterByFileAndFrameId(
                allAnnotationsIncludingDrafts,
                currentMedia?.file.id,
                currentFrameId
            ),
        [allAnnotationsIncludingDrafts, currentMedia?.file.id, currentFrameId]
    );

    // Show slider/thumbnails if there are confirmed annotations OR draft predictions for this file
    const hasAnnotationsOnCurrentFile = useMemo(() => {
        const hasConfirmedAnnotations = hasRealAnnotations(allAnnotationsForCurrentFile);
        return hasConfirmedAnnotations || allAnnotationsIncludingDraftsForCurrentFile.length > 0;
    }, [allAnnotationsForCurrentFile, allAnnotationsIncludingDraftsForCurrentFile]);

    // Reset click count when context changes
    useEffect(() => {
        setFindSimilarClickCount(0);
    }, [textPrompts.length, selectedObject?.id, currentMedia?.file?.id, currentMedia?.frame?.id]);

    // Callback to clear the relabel hint
    const handleRelabelHintDismiss = useCallback(() => {
        setShowRelabelHint(false);
        setRelabelHintThumbnailId(null);
    }, []);

    // Check if there are annotations to clear (vs deleting the entire object)
    const hasAnnotationsToClear = useMemo(() => {
        const hasOnlyPlaceholderAnnotations =
            allAnnotationsIncludingDraftsForCurrentFile.length > 0 &&
            !hasRealAnnotations(allAnnotationsIncludingDraftsForCurrentFile);
        return hasAnnotationsOnCurrentFile && !hasOnlyPlaceholderAnnotations;
    }, [hasAnnotationsOnCurrentFile, allAnnotationsIncludingDraftsForCurrentFile]);

    // Count only annotations with real dimensions (exclude empty placeholders)
    const realAnnotationsCount = useMemo(
        () => countRealAnnotations(visibleAnnotationsForCurrentFile),
        [visibleAnnotationsForCurrentFile]
    );

    // Convert annotations to thumbnails for display
    const thumbnails = useMemo(
        () => annotationsToThumbnails(visibleAnnotationsForCurrentFile),
        [visibleAnnotationsForCurrentFile]
    );

    const handleThumbnailClick = useCallback(
        (e: React.MouseEvent, thumbnail: Thumbnail) => {
            e.stopPropagation(); // Prevent click from bubbling to parent div

            // Block all interactions during model generation
            if (flowLocked) {
                return;
            }

            if (!thumbnail || !thumbnail.frameId || !thumbnail.id || !thumbnail.fileId) {
                console.warn(
                    "Clicked annotation thumbnail is missing frameId, fileId, or id",
                    thumbnail
                );
                return;
            }

            const targetMediaItem = media[thumbnail.fileId];
            const targetFrame = targetMediaItem.isVideo
                ? media[thumbnail.frameId]
                : targetMediaItem;

            if (!targetMediaItem || !targetFrame) {
                console.warn("Target media item or frame not found", thumbnail);
                return;
            }

            setCurrentMedia({
                file: targetMediaItem,
                frame: targetFrame,
                frameSegment: targetFrame.metadata?.segment!
            });

            // Select the annotation on the canvas (shows resize handles)
            // but don't open the label input - that's only for editing the label name
            setSelectedAnnotationId(thumbnail.id);

            // Show relabel hint on first thumbnail click
            if (!hasSeenRelabelHint()) {
                setShowRelabelHint(true);
                setRelabelHintThumbnailId(thumbnail.id);
                markRelabelHintAsSeen();
            }

            // Video seeking will be handled by FilePreview reacting to frame changes
        },
        [flowLocked, media, setSelectedAnnotationId, setCurrentMedia]
    );

    const handleEditAnnotationClick = useCallback(
        (e: React.MouseEvent, thumbnail: Thumbnail) => {
            e.stopPropagation();

            if (flowLocked) {
                return;
            }

            if (!thumbnail || !thumbnail.frameId || !thumbnail.id || !thumbnail.fileId) {
                console.warn(
                    "Edit annotation thumbnail is missing frameId, fileId, or id",
                    thumbnail
                );
                return;
            }

            const targetMediaItem = media[thumbnail.fileId];
            const targetFrame = targetMediaItem.isVideo
                ? media[thumbnail.frameId]
                : targetMediaItem;

            if (!targetMediaItem || !targetFrame) {
                console.warn("Target media item or frame not found", thumbnail);
                return;
            }

            // Navigate to the correct file/frame
            setCurrentMedia({
                file: targetMediaItem,
                frame: targetFrame,
                frameSegment: targetFrame.metadata?.segment!
            });

            // Select the annotation and open the rename input
            setSelectedAnnotationId(thumbnail.id);
            setShouldShowAnnotationInput(true);

            // Clear hint when double-clicking (they're already editing)
            setShowRelabelHint(false);
            setRelabelHintThumbnailId(null);
        },
        [flowLocked, media, setSelectedAnnotationId, setShouldShowAnnotationInput, setCurrentMedia]
    );

    const handleEditClick = useCallback(
        (e?: React.MouseEvent) => {
            e?.stopPropagation(); // Prevent triggering the card click

            // Block all interactions during model generation
            if (flowLocked) {
                return;
            }

            setIsEditing(true);
            setEditValue(name);

            // Focus input after state update
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.select();
                }
            }, 0);
        },
        [flowLocked, inputRef, name]
    );

    const handleObjectCardClick = useCallback(() => {
        // Block all interactions during model generation
        if (flowLocked) {
            return;
        }

        // Set this object as the previously selected object for drawing new bounding boxes
        setPreviouslySelectedObject(name);

        // Find the first annotation of this object type and select it
        // In text prompting mode, we don't navigate to the first annotation
        const firstAnnotationOfThisType = annotationsForThisLabel[0];
        if (false) {
            // Disabled: navigation to first annotation is not needed in text prompting mode
            const targetMediaItem = media[firstAnnotationOfThisType.fileId];
            const targetFrame = targetMediaItem.isVideo
                ? media[firstAnnotationOfThisType.frameId]
                : targetMediaItem;

            if (!targetMediaItem || !targetFrame) {
                console.warn("Target media item or frame not found", firstAnnotationOfThisType);
                return;
            }

            setCurrentMedia({
                file: targetMediaItem,
                frame: targetFrame,
                frameSegment: targetFrame.metadata?.segment!
            });

            setSelectedAnnotationId(firstAnnotationOfThisType.id);
        } else {
            // If no annotations exist for this object type, deselect
            setSelectedAnnotationId(null);
        }
    }, [setPreviouslySelectedObject, setSelectedAnnotationId, flowLocked, name]);

    // Function to rename all annotations and predictions with the old name to the new name
    const handleEditLabel = useCallback(
        (newName: string) => {
            if (newName === name) return;

            // Preserve the old label settings before renaming
            const oldLabelSettings = promptLabels.get(name);

            // Update all annotations with the old name across all files and frames
            setAnnotationsForFileAndFrameId((prev) => {
                const updated = { ...prev };

                Object.keys(updated).forEach((fileId) => {
                    Object.keys(updated[fileId]).forEach((frameId) => {
                        updated[fileId][frameId] = updated[fileId][frameId].map((annotation) => {
                            if (annotation.label === name) {
                                return {
                                    ...annotation,
                                    label: newName,
                                    color: getLabelColor(promptLabels, newName),
                                    empty: false
                                };
                            }
                            return annotation;
                        });
                    });
                });

                return updated;
            });

            // Also update all predictions with the old class name across all files and frames
            // This prevents the old label from being recreated by syncPromptLabels
            setPredictionsForFileAndFrameSegment((prev) => {
                const updated = { ...prev };

                Object.keys(updated).forEach((fileId) => {
                    Object.keys(updated[fileId]).forEach((frameSegment) => {
                        updated[fileId][frameSegment] = updated[fileId][frameSegment].map(
                            (prediction) => {
                                if (prediction.class === name) {
                                    return { ...prediction, class: newName };
                                }
                                return prediction;
                            }
                        );
                    });
                });

                return updated;
            });

            const labelsToKeep = Array.from(promptLabels.keys())
                .filter((labelName) => labelName !== name)
                .concat(newName);

            if (oldLabelSettings) {
                updatePromptLabels(
                    [
                        {
                            name: newName,
                            confidenceThreshold: oldLabelSettings.confidenceThreshold,
                            customColor: oldLabelSettings.customColor
                        }
                    ],
                    [name] // Delete the old name
                );
            } else {
                // If no old settings, just delete the old name
                updatePromptLabels([], [name]);
            }

            // Update previouslySelectedObject so future annotations will use the new name
            setPreviouslySelectedObject(newName);

            // Prevent race conditions where syncPromptLabels might read stale state.
            setTimeout(() => {
                syncPromptLabels(labelsToKeep);
            }, 0);

            // Force a canvas redraw by updating the current frame annotations
            // This ensures the canvas immediately reflects the changes
            setTimeout(() => {
                setCurrentFrameAnnotations((prev: any) => {
                    return [...prev];
                });
            }, 50);
        },
        [
            setAnnotationsForFileAndFrameId,
            setPredictionsForFileAndFrameSegment,
            setPreviouslySelectedObject,
            setCurrentFrameAnnotations,
            name,
            promptLabels,
            updatePromptLabels,
            syncPromptLabels
        ]
    );

    const handleDeleteLabel = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation(); // Prevent triggering the card click

            // Block all interactions during model generation
            if (flowLocked) {
                return;
            }

            if (hasAnnotationsToClear) {
                // Step 1: Clear annotations on current file/frame only
                setAnnotationsForFileAndFrameId((prev) => {
                    return deleteAnnotationsByLabel(
                        prev,
                        name,
                        currentMedia?.file.id,
                        currentFrameId
                    );
                });
            } else {
                // Step 2: Delete all annotations across all files and remove from prompt labels
                setAnnotationsForFileAndFrameId((prev) => {
                    return deleteAnnotationsByLabelCompletely(prev, name);
                });

                // Clear predictions too
                setPredictionsForFileAndFrameSegment((prev: any) => {
                    const updated = { ...prev };
                    Object.keys(updated).forEach((fileId) => {
                        Object.keys(updated[fileId] || {}).forEach((frameSegment) => {
                            if (updated[fileId]?.[frameSegment]) {
                                updated[fileId][frameSegment] = updated[fileId][
                                    frameSegment
                                ].filter((prediction: any) => prediction.class !== name);
                            }
                        });
                    });
                    return updated;
                });

                // Calculate remaining label names before update to avoid stale closure issues.
                const remainingLabelNames = Array.from(promptLabels.keys()).filter(
                    (labelName) => labelName !== name
                );

                updatePromptLabels([], [name]);

                // Ensures the deleted label is removed but other labels without annotations are preserved
                setTimeout(() => {
                    syncPromptLabels(remainingLabelNames);
                }, 0);
            }

            // Clear selection if this object was selected
            if (selectedObject === name || previouslySelectedObject === name) {
                setSelectedAnnotationId(null);
                setPreviouslySelectedObject(null);
            }

            // Force a canvas redraw
            setTimeout(() => {
                setCurrentFrameAnnotations((prev: any) => {
                    return [...prev];
                });
            }, 50);
        },
        [
            flowLocked,
            name,
            selectedObject,
            previouslySelectedObject,
            setSelectedAnnotationId,
            setPreviouslySelectedObject,
            setCurrentFrameAnnotations,
            setAnnotationsForFileAndFrameId,
            setPredictionsForFileAndFrameSegment,
            currentMedia?.file.id,
            promptLabels,
            updatePromptLabels,
            hasAnnotationsToClear,
            syncPromptLabels
        ]
    );

    const handleSaveEdit = useCallback(() => {
        const trimmedValue = editValue.trim().toLowerCase();
        if (trimmedValue && trimmedValue !== name) {
            handleEditLabel(trimmedValue);
        }
        setIsEditing(false);
    }, [editValue, name, handleEditLabel]);

    const handleEditBlur = useCallback(() => {
        handleSaveEdit();
    }, [handleSaveEdit]);

    const handleCancelEdit = useCallback(() => {
        setEditValue(name);
        setIsEditing(false);
    }, [name]);

    const handleEditKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            e.stopPropagation(); // Prevent event from bubbling up to parent div

            if (e.key === "Enter") {
                e.preventDefault();
                handleSaveEdit();
            } else if (e.key === "Escape") {
                handleCancelEdit();
            }
        },
        [handleSaveEdit, handleCancelEdit]
    );

    // Focus input when initially editing
    useEffect(() => {
        if (initiallyEditing) {
            handleEditClick();
        }
    }, [handleEditClick, initiallyEditing, inputRef]);

    const handleDeleteAnnotationClick = useCallback(
        (e: React.MouseEvent, thumbnail: Thumbnail) => {
            e.stopPropagation(); // Important: Stop event from bubbling to parent elements

            // Block all interactions during model generation
            if (flowLocked) {
                return;
            }

            if (!thumbnail || !thumbnail.frameId || !thumbnail.id || !thumbnail.fileId) {
                console.warn(
                    "Attempted to delete annotation missing frameId, fileId, or id",
                    thumbnail
                );
                return;
            }

            // Track annotation deletion from thumbnail
            track({
                event: AnalyticsFlowEvent.ANNOTATION_OBJECT_DELETED,
                data: {
                    appSource: AppSource.RAPID,
                    source: "thumbnail",
                    deletionMethod: "thumbnail_click",
                    annotationId: thumbnail.id,
                    annotationLabel: name,
                    frameId: thumbnail.frameId,
                    fileId: thumbnail.fileId,
                    remainingAnnotations: annotationsForThisLabel.length - 1
                }
            });

            setPendingDelete({
                annotationId: thumbnail.id,
                frameId: thumbnail.frameId as string,
                fileId: thumbnail.fileId
            });

            // Mark this label as having user edits (deletion)
            markLabelAsEdited(name);

            const targetMediaItem = media[thumbnail.fileId];
            const targetFrame = targetMediaItem.isVideo
                ? media[thumbnail.frameId]
                : targetMediaItem;

            if (!targetMediaItem || !targetFrame) {
                console.warn("Target media item or frame not found", thumbnail);
                return;
            }

            setCurrentMedia({
                file: targetMediaItem,
                frame: targetFrame,
                frameSegment: targetFrame.metadata?.segment!
            });

            setSelectedAnnotationId(null);
        },
        [
            flowLocked,
            setSelectedAnnotationId,
            track,
            name,
            annotationsForThisLabel.length,
            setCurrentMedia,
            media,
            markLabelAsEdited
        ]
    );

    // Effect to handle actual deletion once frame navigation is complete
    useEffect(() => {
        if (pendingDelete && currentMedia?.frame?.id === pendingDelete.frameId) {
            setCurrentFrameAnnotations((prevFrameAnnotations: any) => {
                if (!prevFrameAnnotations) return [];
                return prevFrameAnnotations.filter(
                    (anno: any) => anno.id !== pendingDelete.annotationId
                );
            });
            setPendingDelete(null); // Reset pending delete state
        }
    }, [pendingDelete, currentMedia?.frame?.id, setCurrentFrameAnnotations]);

    // Drag and drop handlers
    const handleThumbnailDragStart = useCallback(
        (e: React.DragEvent, thumbnail: Thumbnail) => {
            if (flowLocked) {
                e.preventDefault();
                return;
            }

            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData(
                "text/plain",
                JSON.stringify({
                    annotationId: thumbnail.id,
                    fileId: thumbnail.fileId,
                    frameId: thumbnail.frameId,
                    sourceCardName: name
                })
            );
        },
        [flowLocked, name]
    );

    const handleThumbnailDragEnd = useCallback(() => {
        // Clean up any drag state if needed
    }, []);

    const handleCardDragOver = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (!flowLocked) {
                setIsDragOver(true);
            }
        },
        [flowLocked]
    );

    const handleCardDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        // Only set drag over to false if we're actually leaving the card
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDragOver(false);
        }
    }, []);

    const handleCardDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);

            if (flowLocked) {
                return;
            }

            try {
                const dragData = JSON.parse(e.dataTransfer.getData("text/plain"));
                const { annotationId, fileId, frameId, sourceCardName } = dragData;

                // Don't allow dropping on the same card
                if (sourceCardName === name) {
                    return;
                }

                // Set the previously selected object (similar to AnnotationLabelInput)
                setPreviouslySelectedObject(name);

                // Reassign the annotation to this card's label
                setAnnotationsForFileAndFrameId((prev) => {
                    const updated = { ...prev };

                    // Update the annotation with new label and modifiedAt timestamp (similar to AnnotationLabelInput)
                    if (updated[fileId] && updated[fileId][frameId]) {
                        updated[fileId][frameId] = updated[fileId][frameId].map((annotation) => {
                            if (annotation.id === annotationId) {
                                return {
                                    ...annotation,
                                    label: name,
                                    color: getLabelColor(promptLabels, name),
                                    modifiedAt: Date.now()
                                };
                            }
                            return annotation;
                        });
                    }

                    return updated;
                });

                // Force a canvas redraw
                setTimeout(() => {
                    setCurrentFrameAnnotations((prev) => {
                        return [...prev];
                    });
                }, 50);
            } catch (error) {
                console.error("Error handling drop:", error);
            }
        },
        [
            flowLocked,
            name,
            setAnnotationsForFileAndFrameId,
            setCurrentFrameAnnotations,
            setPreviouslySelectedObject,
            promptLabels
        ]
    );

    const labelColor = getLabelColor(promptLabels, name);

    // Check if THIS specific label has user edits (hasDiffs)
    const thisLabelHasDiffs = useMemo(() => {
        return promptLabels.get(name)?.hasDiffs ?? false;
    }, [promptLabels, name]);

    // Check if this label will use box prompting for inference
    // Box prompting is used when: hasDiffs=true AND there are annotations to use as prompts
    const usesBoxPrompting = useMemo(() => {
        return thisLabelHasDiffs && allAnnotationsForThisLabel.length > 0;
    }, [thisLabelHasDiffs, allAnnotationsForThisLabel.length]);

    const canFindSimilar = useMemo(() => {
        return !flowLocked && hasAnnotationsOnCurrentFile && anyLabelHasDiffs;
    }, [flowLocked, hasAnnotationsOnCurrentFile, anyLabelHasDiffs]);

    const showNoResults = useMemo(() => {
        return (
            !flowLocked &&
            (!hasAnnotationsOnCurrentFile || (anyLabelHasDiffs && thumbnails.length === 0))
        );
    }, [flowLocked, hasAnnotationsOnCurrentFile, anyLabelHasDiffs, thumbnails.length]);

    /**
     * Handler for "Find Similar Objects" button - uses OWLv2 with box prompting.
     *
     * Progressive reveal strategy:
     * - If cache exists (from auto-generate or previous Find Similar): reveal next prediction (instant!)
     * - If no cache: Run OWLv2 inference, cache ALL predictions, show those above threshold
     *
     * This provides a smooth "find more" experience without re-running inference.
     */
    const handleFindSimilarObjects = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();

            if (!currentMedia?.file || !currentMedia?.frame || isFindingSimilar) return;

            const fileId = currentMedia.file.id;
            const frameId = currentMedia.frame.id;

            // Check if we have cached predictions for this file/frame/label (from auto-generate or previous click)
            const cachedPredictions = getBoxPromptCache(fileId, frameId, name);
            const hasCachedPredictions =
                cachedPredictions &&
                cachedPredictions.predictions.length > cachedPredictions.nextToRevealIndex;

            if (hasCachedPredictions) {
                // INSTANT REVEAL: Use cached prediction from auto-generate or previous Find Similar
                const nextPrediction =
                    cachedPredictions.predictions[cachedPredictions.nextToRevealIndex];

                // Get current annotations for this file/frame to check for duplicates
                const currentAnnotationsForNMS = allAnnotationsForCurrentFile.filter(
                    (a) => a.label === name
                );

                // Check if this prediction would be a duplicate using NMS
                const { keptPredictions } = crossClassNMSWithExisting(
                    [nextPrediction],
                    currentAnnotationsForNMS,
                    0.5
                );

                if (keptPredictions.length > 0) {
                    // Get resize ratio for coordinate conversion
                    const mediaItem = currentMedia.file.isVideo
                        ? currentMedia.frame
                        : currentMedia.file;
                    const upload = mediaItem.uploadResizedResult || mediaItem.uploadResult;
                    const resizeRatio = upload?.resizeRatio || 1;

                    // Convert prediction to annotation and add it
                    const processedPrediction = {
                        ...nextPrediction,
                        fileId,
                        frameId: currentMedia.frame.id,
                        // Resize predictions back to original image size
                        width: nextPrediction.width / resizeRatio,
                        height: nextPrediction.height / resizeRatio,
                        x: nextPrediction.x / resizeRatio,
                        y: nextPrediction.y / resizeRatio,
                        class: name,
                        detection_id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
                    };

                    const newAnnotation = predictionToAnnotation(
                        processedPrediction as any,
                        currentMedia.frame.id
                    );

                    // Add the new annotation
                    setAnnotationsForFileAndFrameId((prev) => {
                        const existing = prev[fileId]?.[frameId] || [];
                        return {
                            ...prev,
                            [fileId]: {
                                ...(prev[fileId] || {}),
                                [frameId]: [...existing, newAnnotation]
                            }
                        };
                    });

                    // Force canvas redraw
                    setTimeout(() => {
                        setCurrentFrameAnnotations((prev: any) => [...prev]);
                    }, 50);
                }

                // Update cache index for next click (using store action)
                incrementBoxPromptCacheIndex(fileId, frameId, name);

                // Increment click count for analytics
                setFindSimilarClickCount((prev) => prev + 1);

                track({
                    event: AnalyticsFlowEvent.ANNOTATION_FIND_SIMILAR,
                    data: {
                        appSource: AppSource.RAPID,
                        label: name,
                        fileId,
                        action: "reveal_cached",
                        revealedIndex: cachedPredictions.nextToRevealIndex,
                        confidence: nextPrediction.confidence
                    }
                });

                return;
            }

            // NO CACHE: Run inference and cache all predictions
            // This happens when user clicks Find Similar before auto-generate ran OWLv2 for this label

            // Use strategy to determine which annotations to use as prompts
            const annotationsForBoxPrompting = inferenceStrategy.getBoxPromptingAnnotations(
                allAnnotationsForThisLabel,
                allAnnotationsForCurrentFile
            );

            // Build dimensions map for all frames that have annotations
            const mediaDimensions = new Map<string, { width: number; height: number }>();
            const defaultDimensions =
                currentMedia.file.metadata?.naturalDimensions ||
                currentMedia.file.metadata?.serverMediaDimensions;

            // Use strategy to determine if we should collect image URLs for CDN access
            const shouldCollectUrls = inferenceStrategy.shouldIncludeImageUrls();
            const imageUrls = shouldCollectUrls ? new Map<string, string>() : undefined;

            annotationsForBoxPrompting.forEach((anno) => {
                if (!mediaDimensions.has(anno.frameId)) {
                    const mediaItem = media[anno.fileId];
                    const dimensions =
                        mediaItem?.metadata?.naturalDimensions ||
                        mediaItem?.metadata?.serverMediaDimensions ||
                        defaultDimensions;
                    if (dimensions) {
                        mediaDimensions.set(anno.frameId, dimensions);
                    }
                    // Collect image URL if strategy says to (for CDN images)
                    if (shouldCollectUrls && mediaItem?.file?.imageUrl) {
                        imageUrls?.set(anno.frameId, mediaItem.file.imageUrl);
                    }
                }
            });

            // Build box prompting data using the utility
            // Fixed max 7 prompts per class - no progressive increase
            const boxPromptingData = buildBoxPromptingData(
                annotationsForBoxPrompting,
                mediaDimensions,
                {
                    classFilter: name,
                    workspaceUrl,
                    imageUrls,
                    maxPromptsPerClass: 7 // Fixed at 7, never changes
                }
            );

            if (boxPromptingData.length === 0) {
                console.warn("[FindSimilarObjects] No valid annotations to use as prompts");
                return;
            }

            // Create AbortController for this request
            const abortController = new AbortController();

            setIsFindingSimilar(true);
            setFlowLocked(true); // Lock flow and show loading overlay during inference

            // Clear existing predictions before running inference to prevent stale data
            clearPredictionsForFile(fileId);

            // Wait for all prompt frames to be uploaded before starting OWLv2 inference
            const promptFrameIds = getPromptFrameIds(boxPromptingData);
            if (promptFrameIds.length > 0) {
                try {
                    await waitForPromptFrameUploads(
                        promptFrameIds,
                        () => useStore.getState().media
                    );
                } catch {
                    // Continue anyway - frames might be images that don't need upload
                }
            }

            // Variable to capture raw predictions for caching
            let rawPredictionsForCache: Prediction[] = [];

            try {
                // Count total prompts across all frames
                const promptCount = boxPromptingData.reduce(
                    (total, data) => total + data.boxes.length,
                    0
                );

                // Calculate dynamic confidence threshold based on number of prompts
                const confidenceThreshold = calculateBoxPromptConfidenceThreshold(promptCount);

                // For videos, run inference on the current frame only (not the entire video)
                const mediaItemToInfer = currentMedia.file.isVideo
                    ? currentMedia.frame
                    : currentMedia.file;

                // For videos, we need to pass extractedFrameMeta so predictions are stored correctly
                const extractedFrameMeta = currentMedia.file.isVideo
                    ? { videoId: fileId, segment: currentMedia.frame.metadata?.segment! }
                    : undefined;

                await getPredictionsForFile({
                    mediaItem: mediaItemToInfer,
                    inferenceType: InferenceTypes.OWLVIT,
                    customPromptingData: boxPromptingData,
                    targetLabel: name,
                    useInferenceQueue: true, // Show loading overlay during inference
                    confidenceThreshold,
                    abortController,
                    extractedFrameMeta,
                    fallbackBelowThreshold: true,
                    // Capture raw predictions for caching
                    onRawPredictions: (predictions) => {
                        rawPredictionsForCache = predictions;
                    }
                });

                // Cache ALL predictions (sorted by confidence) for progressive reveal
                // Filter to only predictions for this label and that weren't already shown
                const currentAnnotationsForNMS = allAnnotationsForCurrentFile.filter(
                    (a) => a.label === name
                );

                // Find how many predictions were shown (above threshold or fallback)
                // We need to determine the starting index for next reveal
                let shownCount = 0;
                for (const pred of rawPredictionsForCache) {
                    if (pred.confidence >= confidenceThreshold) {
                        // Check if it would be deduplicated
                        const { keptPredictions } = crossClassNMSWithExisting(
                            [pred],
                            currentAnnotationsForNMS,
                            0.5
                        );
                        if (keptPredictions.length > 0) {
                            shownCount++;
                        }
                    }
                }

                // If no predictions were shown above threshold but we have predictions,
                // the fallback mechanism would have shown one
                if (shownCount === 0 && rawPredictionsForCache.length > 0) {
                    shownCount = 1;
                }

                // Cache to shared store
                setBoxPromptCache(fileId, frameId, name, rawPredictionsForCache, shownCount);

                // Increment click count
                setFindSimilarClickCount((prev) => prev + 1);

                track({
                    event: AnalyticsFlowEvent.ANNOTATION_FIND_SIMILAR,
                    data: {
                        appSource: AppSource.RAPID,
                        label: name,
                        fileId,
                        promptCount,
                        confidenceThreshold,
                        inferenceType: "owlvit",
                        cachedCount: rawPredictionsForCache.length
                    }
                });
            } catch (error: any) {
                if (error?.name === "AbortError" || abortController.signal.aborted) {
                    console.log("[FindSimilarObjects] Request aborted by user");
                } else {
                    console.error("Error finding similar objects:", error);
                }
                clearBoxPromptCacheForLabel(fileId, frameId, name);
            } finally {
                setIsFindingSimilar(false);
                setFlowLocked(false); // Unlock flow after inference completes
            }
        },
        [
            currentMedia?.file,
            currentMedia?.frame,
            isFindingSimilar,
            getPredictionsForFile,
            name,
            track,
            allAnnotationsForThisLabel,
            allAnnotationsForCurrentFile,
            media,
            workspaceUrl,
            clearPredictionsForFile,
            inferenceStrategy,
            getBoxPromptCache,
            setBoxPromptCache,
            incrementBoxPromptCacheIndex,
            clearBoxPromptCacheForLabel,
            setAnnotationsForFileAndFrameId,
            setCurrentFrameAnnotations,
            setFlowLocked
        ]
    );

    // Wrapper to call handleFindSimilarObjects without requiring a mouse event
    // Used by NoResultsEmptyState button
    const handleFindMoreObjects = useCallback(() => {
        // Create a synthetic event-like object with stopPropagation
        const syntheticEvent = { stopPropagation: () => {} } as React.MouseEvent;
        handleFindSimilarObjects(syntheticEvent);
    }, [handleFindSimilarObjects]);

    return (
        <div
            className={twClassNames(
                "promptCard relative",
                flowLocked ? "animate-pulse cursor-not-allowed" : "cursor-default"
            )}
            onClick={handleObjectCardClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onDragOver={handleCardDragOver}
            onDragLeave={handleCardDragLeave}
            onDrop={handleCardDrop}
        >
            {/* Drop indicator */}
            {isDragOver && !flowLocked && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-gray-900 bg-opacity-50">
                    {/* @ts-expect-error - Chip component type issue */}
                    <Chip color="gray" className="!text-primary">
                        Drop here
                    </Chip>
                </div>
            )}
            {/* Header buttons for Annotate mode */}
            {isHovered && !flowLocked && !textPrompts.includes(name) && !isEditing && (
                <div className="absolute right-2 top-2 z-10 flex items-center">
                    {/* @ts-expect-error - Button component type issue */}
                    <TooltipContainer
                        title="Edit object name"
                        type="titleOnly"
                        customButton={
                            /* @ts-expect-error - Button component type issue */
                            <Button
                                onClick={handleEditClick}
                                type="ghost"
                                size="small"
                                shape="circle"
                            >
                                <i className="far fa-pencil"></i>
                            </Button>
                        }
                    />

                    <TooltipContainer
                        title={hasAnnotationsToClear ? "Clear annotations" : "Delete object"}
                        type="titleOnly"
                        customButton={
                            /* @ts-expect-error - Button component type issue */
                            <Button
                                onClick={handleDeleteLabel}
                                type={hasAnnotationsToClear ? "ghost" : "red-ghost"}
                                size="small"
                                shape="circle"
                            >
                                <i
                                    className={
                                        hasAnnotationsToClear
                                            ? "far fa-broom-wide"
                                            : "far fa-trash-alt"
                                    }
                                ></i>
                            </Button>
                        }
                    />
                </div>
            )}
            <div
                className={twClassNames(
                    hasAnnotationsOnCurrentFile &&
                        !showNoResults &&
                        "overflow-hidden border-b border-gray-300"
                )}
            >
                <CardHeader
                    name={name}
                    annotationCount={realAnnotationsCount}
                    mode={BuildPromptSteps.ANNOTATE_DATA}
                    flowLocked={flowLocked}
                    isEditing={isEditing}
                    editValue={editValue}
                    inputRef={inputRef}
                    onEditClick={handleEditClick}
                    onEditKeyDown={handleEditKeyDown}
                    onEditBlur={handleEditBlur}
                    setEditValue={setEditValue}
                    isCardHovered={isHovered}
                    labelColor={labelColor}
                    onColorChange={(newColor) =>
                        updateLabelColor(updatePromptLabels, promptLabels, name, newColor)
                    }
                />
            </div>

            {showNoResults ? (
                <NoResultsEmptyState
                    key={`no-results-${name}`}
                    labelName={name}
                    isBoxPrompting={usesBoxPrompting}
                    onExpandSearch={handleFindMoreObjects}
                    isExpandingSearch={isFindingSimilar}
                />
            ) : (
                <>
                    {/* Show confidence slider only if NO user has edited ANY annotations */}
                    {!flowLocked && hasAnnotationsOnCurrentFile && !anyLabelHasDiffs && (
                        <ConfidenceSlider
                            initialConfidenceThreshold={initialConfidenceThreshold}
                            onConfidenceThresholdChanged={(newThreshold) => {
                                const promptLabel = promptLabels.get(name);
                                updatePromptLabels([
                                    {
                                        name,
                                        confidenceThreshold: newThreshold,
                                        customColor: promptLabel?.customColor
                                    }
                                ]);
                            }}
                            name={name}
                            predictionCount={visibleAnnotationsForCurrentFile.length}
                            mode={BuildPromptSteps.REVIEW_PREDICTIONS}
                            className="pb-0"
                        />
                    )}

                    {!(flowLocked && annotationsForThisLabel.length === 0) &&
                        hasAnnotationsOnCurrentFile && (
                            <CardThumbnails
                                name={name}
                                mode={BuildPromptSteps.REVIEW_PREDICTIONS}
                                thumbnails={thumbnails}
                                selectedObject={selectedObject}
                                previouslySelectedObject={previouslySelectedObject}
                                flowLocked={flowLocked}
                                isSliderVisible={!flowLocked && !anyLabelHasDiffs}
                                onThumbnailClick={handleThumbnailClick}
                                onEditAnnotationClick={handleEditAnnotationClick}
                                onDeleteAnnotationClick={handleDeleteAnnotationClick}
                                onThumbnailDragStart={handleThumbnailDragStart}
                                onThumbnailDragEnd={handleThumbnailDragEnd}
                                labelColor={labelColor}
                                showRelabelHint={showRelabelHint}
                                relabelHintThumbnailId={relabelHintThumbnailId}
                                onRelabelHintDismiss={handleRelabelHintDismiss}
                            />
                        )}

                    {/* Show "Find Similar Objects" button if ANY annotation has been edited */}
                    {canFindSimilar && (
                        <div className="flex items-center justify-end gap-2 px-1 pb-1">
                            {/* @ts-expect-error - Button component type issue */}
                            <Button
                                type="purbo-ghost"
                                size="small"
                                onClick={handleFindSimilarObjects}
                                disabled={isFindingSimilar || flowLocked}
                                className="w-44 justify-center"
                            >
                                {isFindingSimilar ? (
                                    <>
                                        <i className="far fa-spinner-third fa-spin"></i>
                                        Finding Objects...
                                    </>
                                ) : (
                                    <>
                                        <i className="far fa-sparkles"></i>
                                        {findSimilarClickCount > 0
                                            ? "Find More Objects"
                                            : "Find Similar Objects"}
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
